import { css } from 'aphrodite';
import * as React from 'react';
import { DraggableData, DraggableEvent } from 'react-draggable';
import { AIChatRole } from '../../../../../../extViewmodels';
import { useAIConversationChatMessageQuery } from '../../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { AIMarkdownResponse } from '../../../../../../web/components-v2/ai/AiMarkdownResponse';
import { Button } from '../../../../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../../../../web/components/LoadingSpinner';
import { SparklesIcon } from '../../../../../../web/components/svgs/icons/SparklesIcon';
import { XIcon } from '../../../../../../web/components/svgs/icons/XIcon';
import { brandPrimary, charmGray } from '../../../../../../web/styles/colors';
import { AidaDraggable } from '../../../../../components/AidaDraggable';
import { DraggableIcon } from '../../../../../components/svgs/icons/audio/DraggableIcon';
import { useMultiDemoAnalysis } from '../../hooks/useMultiDemoAnalysis';
import { styleSheet } from './styles';

interface DemoAnalysisModalProps {
	isOpen: boolean;
	onClose: () => void;
	isLoading: boolean;
	isCreatingConversation: boolean;
	onSendFollowUp: (question: string) => void;
	conversationId: string;
	aiPromptMutation: ReturnType<typeof useMultiDemoAnalysis>['aiPromptMutation'];
}

export const DemoAnalysisModal: React.FC<DemoAnalysisModalProps> = ({
	isOpen,
	onClose,
	isLoading,
	isCreatingConversation,
	onSendFollowUp,
	conversationId,
	aiPromptMutation,
}) => {
	const conversationChatMessageQuery = useAIConversationChatMessageQuery({
		id: conversationId,
		enabled: Boolean(conversationId),
		staleTime: Infinity,
	});
	const [followUpQuestion, setFollowUpQuestion] = React.useState('');
	const [modalDragging, setModalDragging] = React.useState<{
		active?: boolean;
		x?: number;
		y?: number;
	}>({ active: false, x: 0, y: 0 });

	// Load modal position from localStorage on component mount
	React.useEffect(() => {
		try {
			const savedPosition = localStorage.getItem('demoAnalysisModalPosition');
			if (savedPosition) {
				const { x, y } = JSON.parse(savedPosition);
				setModalDragging(prev => ({ ...prev, x, y }));
			}
		} catch (error) {
			console.error('Error loading demo analysis modal position from localStorage:', error);
		}
	}, []);

	const onModalDrag = (event: DraggableEvent, { x, y }: DraggableData) => {
		event.stopPropagation();
		setModalDragging({ active: true, x, y });
	};

	const onModalDragStop = (_: DraggableEvent, { x, y }: DraggableData) => {
		const { active } = modalDragging;
		if (active) {
			setModalDragging({ active: false, x, y });

			// Save position to localStorage when drag stops
			try {
				localStorage.setItem('demoAnalysisModalPosition', JSON.stringify({ x, y }));
			} catch (error) {
				console.error('Error saving demo analysis modal position to localStorage:', error);
			}
		}
	};

	const handleSendFollowUp = React.useCallback(() => {
		if (!followUpQuestion.trim()) return;

		onSendFollowUp(followUpQuestion);
		setFollowUpQuestion('');
	}, [followUpQuestion, onSendFollowUp]);

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSendFollowUp();
		}
	};

	if (!isOpen) return null;

	return (
		<AidaDraggable
			onDrag={onModalDrag}
			onStop={onModalDragStop}
			defaultPosition={{ x: modalDragging.x, y: modalDragging.y }}
			position={{ x: modalDragging.x, y: modalDragging.y }}
			handle='.demo-analysis-drag-handle'
			isDraggable={true}
		>
			<div className={css(styleSheet.modalContainer)}>
				<div className={css(styleSheet.modalHeader)}>
					<div className={css(styleSheet.modalHeaderContent)}>
						<SparklesIcon className={css(styleSheet.modalIcon)} />
						<h3 className={css(styleSheet.modalTitle)}>Demo Analysis</h3>
					</div>
					<div className={css(styleSheet.modalHeaderActions)}>
						<div className='demo-analysis-drag-handle'>
							<DraggableIcon fill={brandPrimary} />
						</div>
						<button onClick={onClose} className={css(styleSheet.iconButton)}>
							<XIcon color={charmGray} height={12} width={12} />
						</button>
					</div>
				</div>
				<div className={css(styleSheet.modalContent)}>
					{isCreatingConversation ? (
						<div className={css(styleSheet.loadingContainerWithColor)}>
							<LoadingSpinner type='small' />
							<div className={css(styleSheet.loadingText)}>Creating AI Conversation...</div>
						</div>
					) : (
						<>
							<div className={css(styleSheet.conversationContainer)}>
								{conversationChatMessageQuery.data?.map((message, index) => {
									return (
										<div
											key={index}
											className={css(
												styleSheet.messageContainer,
												message.role === AIChatRole.User ? styleSheet.userMessage : styleSheet.assistantMessage
											)}
										>
											<div className={css(styleSheet.messageRole)}>
												{message.role === AIChatRole.User ? 'You' : 'AI Assistant'}
											</div>
											<div className={css(styleSheet.messageContent)}>
												<AIMarkdownResponse>{message.text}</AIMarkdownResponse>
											</div>
										</div>
									);
								})}
								{aiPromptMutation.isLoading ? (
									<div className={css(styleSheet.messageContainer, styleSheet.assistantMessage)}>
										<div className={css(styleSheet.messageRole)}>AI Assistant</div>
										<div className={css(styleSheet.loadingContainer)}>
											<LoadingSpinner type='small' />
											<div className={css(styleSheet.loadingText)}>Thinking...</div>
										</div>
									</div>
								) : null}
							</div>

							{/* Follow-up question input - only show when not loading */}
							{!isLoading && (
								<div className={css(styleSheet.inputContainer)}>
									<textarea
										className={css(styleSheet.textarea)}
										value={followUpQuestion}
										onChange={e => setFollowUpQuestion(e.target.value)}
										onKeyPress={handleKeyPress}
										placeholder={`Ask a follow-up question about the demo analysis...`}
										disabled={isLoading}
										rows={3}
									/>
									<div className={css(styleSheet.buttonContainer)}>
										<Button
											onClick={handleSendFollowUp}
											disabled={
												isLoading ||
												aiPromptMutation.isLoading ||
												aiPromptMutation.isStreaming ||
												!followUpQuestion.trim()
											}
										>
											Send Question
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</AidaDraggable>
	);
};
